import { defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";

import { PatientPayorTypeEnum } from "../../../../../../../enums/PatientPayorEnum";
import { IGetMedicareIdsQuery } from "../../../../../../../resources-vue/vue-interfaces/i-get-payor";
import { EnumsService } from "../../../../../../../services/enums.service";
import { CentralIntakeService } from "../../../../../../../services/central-intake.service";
import { ToastrService } from "../../../../../../../services/toastr.service";
import { useHospiceConvertToPending } from "../../../../store/useHospiceConvertToPending";
import { IPageCallbackResult } from "../../../../../../../resources-vue/vue-interfaces/i-page-container";
import { PAGE_FAIL_RESPONSE } from "../../../../../../../resources-vue/vue-elements/Pagination/Pagination";
import { PayerTypes } from "../../../../../../../enums/enums";
import { HospicePatientPayer } from "../../../../../../../resources-vue/vue-models/hospice-patient-payer";

import SortableTh from "../../../../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import ErrorMessageHandler from "../../../../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import PayerRow from "../PayerRow/PayerRow.vue";
import Pagination from "../../../../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import PayerForm from "../PayerForm/PayerForm.vue";

export default defineComponent({
    components: {
        SortableTh,
        ErrorMessageHandler,
        PayerRow,
        Pagination,
        PayerForm
    },

    props: {
        addNewPayer: {
            type: Function
        },
        getCurrentPatientPayers: {
            type: Function
        },
        getPatientPayers: {
            type: Function
        },
        updatePatientPayer: {
            type: Function
        },
        deletePatientPayer: {
            type: Function
        }
    },
    data() {
        return {
            _enumsService: null,
            _centralIntakeService: null,
            _toastrService: null,
            isAddPayerRequested: false as boolean,
            isCurrent: true as boolean,
            payersPagination: null,
            selectedType: PatientPayorTypeEnum.All as string,
            patientPayerOptions: Object.values(PatientPayorTypeEnum) as Array<string>,
            newPatientPayer: null,
            isAddPayerInProgress: false as boolean,
            medicareIds: [] as Array<any>,
            isLoading: false as boolean,
            sortBy: "type" as string,
            sortOrder: "desc" as string,
            page: null,
            payorTypeEnum: PayerTypes,
            isError: false as boolean,
            columns: 7 as number
        };
    },

    created() {
        this._enumsService = Container.instance.get(EnumsService);
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    async mounted() {
        this.newPatientPayer = new HospicePatientPayer({});
        try {
            const getMedicareIdQuery: IGetMedicareIdsQuery = {
                application: Number(this.application),
                agencyId: this.accountId as string,
                accountId: this.accountId as string,
                providerIds: [this.patientDetails.providerId]
            };
            this.medicareIds = (await this._centralIntakeService.getMedicareIds(getMedicareIdQuery)).map(
                (med: any) => med.id
            );
        } catch (e) {
            console.error(e);
        }
        this.payersPagination = this.$refs.payersPagination;
        this.resetList();
    },

    computed: {
        ...mapState(useHospiceConvertToPending, ["accountId", "application", "patientDetails", "patientId"]),
        hasData() {
            return this.page && this.page.ItemCount > 0;
        },
        loadingCount() {
            return this.page && this.page.Items.length > 0 ? this.page.Items.length : 3;
        }
    },

    watch: {
        selectedType() {
            this.selectedTypeChanged();
        }
    },

    methods: {
        setIsCurrent(value: boolean) {
            this.isCurrent = value;
            this.resetList();
        },
        resetList() {
            this.payersPagination?.reset();
        },
        selectedTypeChanged() {
            this.resetList();
        },
        openAddPayer() {
            this.isAddPayerRequested = true;
        },
        async saveNewPayer() {
            const validated = await (this.$refs.payerForm as any).v$.$validate();
            if (validated) {
                try {
                    this.isAddPayerInProgress = true;
                    this.newPatientPayer.AccountId = this.accountId;
                    this.newPatientPayer.Application = Number(this.application);
                    const response = await this.addNewPayer(this.newPatientPayer);
                    if (response.isSuccess) {
                        this._toastrService.success({
                            title: `Payer Added`,
                            message: `The payer ${this.newPatientPayer.AgencyPayorName} has been successfully added.`
                        });
                        this.closeAddPayer();
                        await this.resetList();
                    } else {
                        this._toastrService.error({
                            title: `Failed to add Payer`,
                            message: `${JSON.parse(response.message).message}`
                        });
                    }
                } catch (e) {
                    this.openAddPayer();
                } finally {
                    this.isAddPayerInProgress = false;
                }
            } else {
                this._toastrService.error({
                    title: `Validation Error`,
                    message: `Pleaae fill required fields`
                });
            }
        },
        closeAddPayer() {
            this.isAddPayerRequested = false;
            this.newPatientPayer = new HospicePatientPayer({});
        },
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            try {
                this.isLoading = true;
                let data;
                let query = {
                    page: pageNumber,
                    pageLength: pageSize,
                    sortBy: this.sortBy,
                    order: this.sortOrder,
                    application: Number(this.application),
                    accountId: this.accountId
                };
                if (this.isCurrent) {
                    data = await this.getCurrentPatientPayers(query);
                    this.page = data;
                } else {
                    data = await this.getPatientPayers({ query: { ...query, payorTypes: this.getQueryPayerTypes() } });
                    this.page = data.data;
                }

                if (!this.page?.Items) {
                    this.page.Items = [];
                }
                this.isLoading = false;
                return {
                    totalPageCount: data.PageCount,
                    success: data.ItemCount > 0,
                    totalCount: data.ItemCount
                };
            } catch (e) {
                console.error(e);
                this.isLoading = false;
                return PAGE_FAIL_RESPONSE;
            }
        },
        getQueryPayerTypes() {
            let primaryPayerValue = this.payorTypeEnum.getValueUsingKey("Primary") as number;
            let secondaryPayerValue = this.payorTypeEnum.getValueUsingKey("Secondary") as number;
            let tertiaryPayerValue = this.payorTypeEnum.getValueUsingKey("Tertiary") as number;
            let roomAndBoardPayerValue = this.payorTypeEnum.getValueUsingKey("RoomAndBoard") as number;
            let palliativePayerValue = this.payorTypeEnum.getValueUsingKey("PalliativeCare") as number;
            let vbidPayerValue = this.payorTypeEnum.getValueUsingKey("VBID") as number;
            switch (this.selectedType) {
                case PatientPayorTypeEnum.Primary:
                    return [primaryPayerValue];
                case PatientPayorTypeEnum.Secondary:
                    return [secondaryPayerValue];
                case PatientPayorTypeEnum.Tertiary:
                    return [tertiaryPayerValue];
                case PatientPayorTypeEnum.RoomAndBoard:
                    return [roomAndBoardPayerValue];
                case PatientPayorTypeEnum.PalliativeCare:
                    return [palliativePayerValue];
                case PatientPayorTypeEnum.Vbid:
                    return [vbidPayerValue];
                case PatientPayorTypeEnum.All:
                    return [
                        primaryPayerValue,
                        secondaryPayerValue,
                        tertiaryPayerValue,
                        roomAndBoardPayerValue,
                        palliativePayerValue,
                        vbidPayerValue
                    ];
                default:
                    return [];
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.resetList();
            }
        },
        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.resetList();
            }
        }
    }
});
